import { useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import * as service from '../services';
import { useDebounce } from '../../../../../hooks';
import { useAnaliseCoberturaStore, coverageInitialState } from '../AnaliseCobertura.store';
import { Filter, SectionContent } from '../types';

export const useCoverageAnalysisSection = () => {
  const { setCoverageAnalysisState, sectionCoverageParams, section } = useAnaliseCoberturaStore();

  const debounce = useDebounce(500);

  const sectionCoverageSuccess = (data: AxiosResponse) => {
    setCoverageAnalysisState('sectionCoverageData', data.data);
  };

  const mutation = useMutation({
    mutationFn: data => service.getCoverageAnalysisSection(data),
    onSuccess: data => sectionCoverageSuccess(data),
  });

  const handleChangeSectionCoveragePeriod = (value: number) => {
    const param = { ...sectionCoverageParams, diasAnalise: value };
    setCoverageAnalysisState('sectionCoverageParams', param);
    mutation.mutateAsync(param);
  };

  const handleChangeSectionCoveragePagination = (value: number) => {
    const param = { ...sectionCoverageParams, page: value - 1 };
    setCoverageAnalysisState('sectionCoverageParams', param);
    mutation.mutateAsync(param);
  };

  // FILTER

  const setList = (data: SectionContent[]) => {
    const state = useAnaliseCoberturaStore.getState();
    const list = data?.map(item => ({
      value: item.secao,
      label: item.secao,
    }));

    const prev = state.section;
    const cacheList = prev.cache;
    const removeDuplicate = list.filter(i => !prev.value?.includes(i.value));
    const concatList = prev.value?.length ? cacheList.concat(removeDuplicate) : list;
    setCoverageAnalysisState('section', { ...prev, list: concatList });
  };

  const handleChangeSectionCoverageSearch = async (value: string) => {
    const param = { ...sectionCoverageParams, filtro: [], query: value, page: 0, size: 50 };
    debounce(async () => {
      const resp = await service.getCoverageAnalysisSection(param);

      if (resp?.status === 200) {
        setList(resp?.data.content);
      }
    });
  };

  const addSecao = (item: Filter) => {
    const param = sectionCoverageParams;
    const prev = section;

    if (!prev.value.includes(item.value)) {
      const value = [...prev.value, item.value];
      const cache = [...(prev.cache || []), item];
      setCoverageAnalysisState('section', { ...prev, value, cache });
      setCoverageAnalysisState('sectionCoverageParams', { ...param, filtro: value });
    }
  };

  const removeSecao = (item: Filter) => {
    const param = sectionCoverageParams;
    const prev = section;

    const value = prev.value.filter(value => value !== item.value);
    const cache = prev.cache?.filter(cache => cache.value !== item.value);
    setCoverageAnalysisState('section', { ...prev, value, cache });
    setCoverageAnalysisState('sectionCoverageParams', { ...param, filtro: value });
  };

  const handleSelectSectionCoverageFilter = (item: Filter) => {
    const prev = section;
    if (prev.value.includes(item.value)) {
      removeSecao(item);
    } else {
      addSecao(item);
    }
  };

  const handleCleanSectionCoverageFilter = () => {
    const initialState = coverageInitialState.sectionCoverageParams;
    const param = sectionCoverageParams;
    const prev = section;
    setCoverageAnalysisState('section', { ...prev, value: [], cache: [] });
    setCoverageAnalysisState('sectionCoverageParams', { ...param, filtro: [] });
    mutation.mutateAsync({ ...initialState, diasAnalise: param.diasAnalise, filtro: [] });
  };

  useEffect(() => {
    mutation.mutateAsync(sectionCoverageParams);
  }, []);

  return {
    SectionCoverageLoading: mutation.isPending,
    handleChangeSectionCoveragePeriod,
    handleChangeSectionCoverageSearch,
    handleChangeSectionCoveragePagination,
    handleSelectSectionCoverageFilter,
    handleCleanSectionCoverageFilter,
    handleRequestSectionCoverageTableList: mutation.mutateAsync,
  };
};
